import React, { useEffect, useState } from 'react'
import AppPopover from '../../../../UIComponents/ModalShell/AppPopover'
import { useDispatch, useSelector } from 'react-redux'

import TagsInteractionPopup from '../../../../NotesView/NotesDV/EditorView/TagsInteractionPopup'
import { exportRef } from '../../../../NotesView/NotesDV/EditorView/NotesEditorView'
import { quillTextInputIsCalendarTask, quillTextInputRefs } from '../../CustomTextInput3'
import { addProtocol, formatUrl, getUrlObject, isValidPreConfigTaskLink } from '../../../../../utils/LinkingHelper'
import { colors } from '../../../../styles/global'
import { getQuillEditorRef } from '../../textInputHelper'
import URLTrigger from '../../../../../URLSystem/URLTrigger'
import NavigationService from '../../../../../utils/NavigationService'
import LinkTag, { getPathname } from '../../../../Tags/LinkTag'
import { getAssistantData } from '../../../../../utils/backends/Assistants/assistantsFirestore'
import { resolvePreConfigTaskLink } from '../../../../../utils/preConfigTaskLink'
import { getAssistant, GLOBAL_PROJECT_ID, isGlobalAssistant } from '../../../../AdminPanel/Assistants/assistantsHelper'
import { setPreConfigTaskModalData, setIframeModalData } from '../../../../../redux/actions'
import { generateTaskFromPreConfig } from '../../../../../utils/assistantHelper'
import {
    COMMENT_MODAL_ID,
    exitsOpenModals,
    FOLLOW_UP_MODAL_ID,
    getModalParams,
    MANAGE_TASK_MODAL_ID,
    removeModal,
    storeModal,
    TAGS_EDIT_OBJECT_MODAL_ID,
    TASK_DESCRIPTION_MODAL_ID,
    WORKFLOW_MODAL_ID,
} from '../../../../ModalsManager/modalsManager'
import { REGEX_URL } from '../../../Utils/HelperFunctions'
import ReactQuill from 'react-quill-new'
import EditObjectsInLinks from '../../../../EditObjectsInLinks/EditObjectsInLinks'
import { isLegacyEmailUrlFragment } from './legacyEmailUrlFragment'
import { translate } from '../../../../../i18n/TranslationService'

const Delta = ReactQuill.Quill.import('delta')

export default function UrlWrapper({ value, objectName, isShared, embedNode }) {
    const dispatch = useDispatch()
    const virtualQuillLoaded = useSelector(state => state.virtualQuillLoaded)
    const projectId = useSelector(state => state.quillEditorProjectId)
    const loggedUser = useSelector(state => state.loggedUser)
    const activeNoteIsReadOnly = useSelector(state => state.activeNoteIsReadOnly)
    const activeNoteId = useSelector(state => state.activeNoteId)
    let { url = '', type = '', id: tagId = '', editorId = '', userIdAllowedToEditTags = '' } = value
    const { editorRef, inNote } = getQuillEditorRef(exportRef, quillTextInputRefs, editorId)
    const [isOpen, setIsOpen] = useState(false)
    const [canceled, setCanceled] = useState(false)
    const [userIsMember, setUserIsMember] = useState(true)
    const [objectType, setObjectType] = useState(null)
    const [objectData, setObjectData] = useState(null)
    const [objectProjectId, setObjectProjectId] = useState(null)
    const [isPrivate, setIsPrivate] = useState(false)
    const [showEditObjectPopup, setShowEditObjectPopup] = useState(false)
    const [renderAsLegacyEmailText, setRenderAsLegacyEmailText] = useState(false)
    const inReadOnlyNote = activeNoteId !== '' && (activeNoteIsReadOnly || loggedUser.isAnonymous)

    useEffect(() => {
        if (value.open?.toString() === 'true' && !virtualQuillLoaded) {
            openModal()
        }
    }, [])

    useEffect(() => {
        // Older generated notes stored the local part and domain of an email address as separate
        // URL embeds. The converter now prevents that for new notes; this read-time compatibility
        // path makes already stored notes render as normal email text without mutating their CRDT.
        const timeout = setTimeout(() => {
            setRenderAsLegacyEmailText(isLegacyEmailUrlFragment(embedNode, url))
        }, 0)
        return () => clearTimeout(timeout)
    }, [embedNode, url])

    useEffect(() => {
        if (!isOpen && canceled && !virtualQuillLoaded) {
            // This time out is needed because
            // the clean format should be done after the popup is gone from the DOM
            // Otherwise the popup will remain in the view as a Ghost element
            setTimeout(() => cleanTextFormat(), 1000)
        }
    }, [isOpen])

    useEffect(() => {
        setTimeout(() => {
            setShowEditObjectPopup(
                (objectType != null &&
                    objectData != null &&
                    getModalParams(MANAGE_TASK_MODAL_ID) == null &&
                    getModalParams(TAGS_EDIT_OBJECT_MODAL_ID) == null) ||
                    isPrivate ||
                    // To check if current edit modal opened is an Edit Task
                    getModalParams(MANAGE_TASK_MODAL_ID)?.fromUrlTag?.[objectData?.id] === 1
            )
        }, 400)
    }, [objectType, objectData, isPrivate])

    const needShortTags = () => {
        const manageTaskModalData = getModalParams(MANAGE_TASK_MODAL_ID)
        return manageTaskModalData && manageTaskModalData.editorId === editorId
    }

    const openModal = e => {
        // For pre-configured tasks, directly execute the action (open the modal)
        if (type === 'preConfigTask' || isValidPreConfigTaskLink(url, projectId)) {
            performAction(url)
            return
        }

        const isSkillAndLoggedUserIsNotOwner =
            objectType === 'skill' && (loggedUser.isAnonymous || objectData.userId !== loggedUser.uid)
        if (
            !isSkillAndLoggedUserIsNotOwner &&
            !inReadOnlyNote &&
            (!userIdAllowedToEditTags ||
                userIdAllowedToEditTags === 'null' ||
                userIdAllowedToEditTags === 'undefined' ||
                userIdAllowedToEditTags === loggedUser.uid)
        ) {
            const taskModalParam = getModalParams(MANAGE_TASK_MODAL_ID)?.fromUrlTag
            if (objectType != null && objectData != null && objectType === 'task' && taskModalParam == null) {
                // Register current Edit Task popup opened
                storeModal(MANAGE_TASK_MODAL_ID, { inTag: true, fromUrlTag: { [objectData?.id]: 1 } })
            }
            setIsOpen(true)
        } else {
            performAction(url)
        }
    }

    const closeModal = (cleanFormat = false) => {
        if (cleanFormat && type === 'plain') {
            setCanceled(true)
        }
        const taskModalParam = getModalParams(MANAGE_TASK_MODAL_ID)?.fromUrlTag
        if (
            objectType != null &&
            objectData != null &&
            objectType === 'task' &&
            taskModalParam?.[objectData.id] === 1
        ) {
            // Unregister the "unique" Edit Task popup opened
            removeModal(MANAGE_TASK_MODAL_ID)
        }
        setIsOpen(false)
        resetOpen()
    }

    const performAction = async currentUrl => {
        if (url.trim() !== currentUrl.trim()) {
            updateUrl(currentUrl)
        }

        // Handle pre-configured task links - open the task generator modal
        const isPreConfig = type === 'preConfigTask' || isValidPreConfigTaskLink(currentUrl, projectId)

        if (isPreConfig) {
            try {
                const resolved = await resolvePreConfigTaskLink(currentUrl, projectId)
                if (resolved) {
                    const { task, assistantId, assistantProjectId, targetProjectId } = resolved
                    closeModal()
                    const taskType = task.type || 'prompt'

                    // Handle different task types
                    if (taskType === 'prompt' || taskType === 'webhook') {
                        // For prompt/webhook: show modal if has variables, otherwise execute directly
                        if (task.variables && task.variables.length > 0) {
                            // Get assistant from Redux, or fetch from backend
                            let assistant = getAssistant(assistantId)

                            if (!assistant) {
                                const fetchProjectId = isGlobalAssistant(assistantId)
                                    ? GLOBAL_PROJECT_ID
                                    : assistantProjectId
                                assistant = await getAssistantData(fetchProjectId, assistantId)
                            }

                            if (assistant) {
                                dispatch(setPreConfigTaskModalData(true, task, assistant, targetProjectId))
                            }
                        } else {
                            // No variables - execute directly
                            const aiSettings =
                                task.aiModel || task.aiReasoningEffort !== undefined || task.aiSystemMessage
                                    ? {
                                          model: task.aiModel,
                                          reasoningEffort: task.aiReasoningEffort,
                                          systemMessage: task.aiSystemMessage,
                                      }
                                    : null
                            const taskMetadata = {
                                ...(task.taskMetadata || {}),
                                sendWhatsApp: !!task.sendWhatsApp,
                                executionMode: task.executionMode,
                            }
                            generateTaskFromPreConfig(
                                targetProjectId,
                                task.name,
                                assistantId,
                                task.prompt,
                                aiSettings,
                                taskMetadata
                            )
                        }
                    } else if (taskType === 'iframe') {
                        // Open iframe modal
                        dispatch(setIframeModalData(true, task.link, task.name))
                    } else if (taskType === 'link') {
                        // Open external link in new tab
                        window.open(task.link, '_blank')
                    } else {
                        // Unknown type - try to open as link
                        if (task.link) {
                            window.open(task.link, '_blank')
                        }
                    }
                } else {
                    window.alert(translate('pre_config_task_link_unavailable'))
                }
            } catch (error) {
                console.error('[UrlWrapper] Error opening pre-configured task:', error)
                window.alert(translate('pre_config_task_link_load_failed'))
            }
            return
        }

        if (type !== 'plain' && type !== 'preConfigTask' && getModalParams(TAGS_EDIT_OBJECT_MODAL_ID) == null) {
            if (!loggedUser.isAnonymous || isShared) {
                closeModal()
                setTimeout(() => {
                    URLTrigger.processUrl(NavigationService, getPathname(currentUrl))
                }, 400)
            }
        } else if (type !== 'preConfigTask') {
            window.open(addProtocol(currentUrl), '_blank')
        }
    }

    const updateUrl = newUrl => {
        newUrl = addProtocol(newUrl)
        const editor = editorRef.getEditor()
        closeModal()
        setTimeout(function () {
            const ops = editor.getContents().ops
            let tagPosition = 0
            for (let i = 0; i < ops.length; i++) {
                const insert = ops[i].insert

                if (insert && insert.url && insert.url.id === tagId) {
                    const rootUrl = formatUrl(newUrl)
                    if (rootUrl) {
                        const newUrlObj = getUrlObject(
                            newUrl,
                            rootUrl,
                            objectProjectId,
                            editorId,
                            userIdAllowedToEditTags
                        )
                        const { type, objectId } = newUrlObj
                        const delta = new Delta()

                        delta.retain(tagPosition)
                        if (insert.url.url) {
                            delta.delete(1)
                        } else {
                            const delta2 = new Delta()
                            delta2.retain(tagPosition)
                            delta2.delete(1)
                            editor.updateContents(delta2)
                        }

                        const isObjectNoteUrl = newUrl && newUrl.endsWith('/note')
                        if (type === 'task' && inNote && !isObjectNoteUrl) {
                            const taskTagFormat = { id: tagId, taskId: objectId, editorId, objectUrl: url }
                            delta.insert({ taskTagFormat })
                        } else {
                            const url = { ...newUrlObj }
                            url.id = tagId
                            url.open = false
                            delta.insert({ url })
                        }

                        editor.updateContents(delta, 'user')
                        editor.setSelection(tagPosition + 1, 0, 'user')
                    }
                    break
                }

                tagPosition += typeof insert === 'string' ? insert.length : 1
            }
        }, 400)
    }

    const resetOpen = () => {
        setTimeout(function () {
            if (typeof editorRef?.getEditor !== 'function' || activeNoteId === '') return
            try {
                const editor = editorRef?.getEditor?.()
                const ops = editor.getContents().ops
                let tagPosition = 0
                for (let i = 0; i < ops.length; i++) {
                    const insert = ops[i].insert

                    if (insert && insert.url && insert.url.id === tagId && !insert.url.url) {
                        const url = { ...insert.url }
                        url.open = false

                        const delta = new Delta()
                        delta.retain(tagPosition)
                        delta.delete(1)
                        delta.insert({ url })
                        editor.updateContents(delta)
                        editor.setSelection(tagPosition + 1, 0)
                        break
                    }

                    tagPosition += typeof insert === 'string' ? insert.length : 1
                }
            } catch (error) {
                console.warn('Failed to replace the url tag in the editor', error)
            }
        }, 400)
    }

    const cleanTextFormat = () => {
        const editor = exportRef?.getEditor()

        if (editor) {
            const ops = editor.getContents().ops
            let length = 0
            for (let i = 0; i < ops.length; i++) {
                if (ops[i].insert) {
                    if (typeof ops[i].insert === 'object') {
                        if (ops[i].insert && ops[i].insert.url && ops[i].insert.url.id === tagId) {
                            const newDelta = new Delta()
                            newDelta.retain(length).delete(2)
                            editor.updateContents(newDelta)
                            break
                        }
                        length += 1
                    } else {
                        length += ops[i].insert.length
                    }
                }
            }
        }
    }

    const validateUrl = text => {
        return REGEX_URL.test(addProtocol(text))
    }

    const closeWhenClickOutsideModal = () => {
        const modalExceptions = [
            COMMENT_MODAL_ID,
            MANAGE_TASK_MODAL_ID,
            FOLLOW_UP_MODAL_ID,
            WORKFLOW_MODAL_ID,
            TASK_DESCRIPTION_MODAL_ID,
            TAGS_EDIT_OBJECT_MODAL_ID,
        ]

        if ((objectType == null && objectData == null) || !showEditObjectPopup || !exitsOpenModals(modalExceptions)) {
            // dismissAllPopups()
            if (!REGEX_URL.test(url) && exportRef) {
                closeModal(true)
            } else {
                closeModal()
            }
        }
    }

    if (renderAsLegacyEmailText) {
        return <span data-legacy-email-fragment="true">{url}</span>
    }

    return (
        <AppPopover
            content={
                showEditObjectPopup ? (
                    <EditObjectsInLinks
                        projectId={objectProjectId || projectId}
                        objectType={objectType}
                        objectData={objectData}
                        userIsMember={userIsMember}
                        closeModal={closeModal}
                        editorId={editorId}
                        editorRef={editorRef}
                        tagId={tagId}
                        isPrivate={isPrivate}
                        objectUrl={url}
                        inNote={inNote}
                    />
                ) : (
                    <TagsInteractionPopup
                        ico="link"
                        inputTextColor={colors.Primary100}
                        initialValue={url}
                        performAction={performAction}
                        closeModal={closeModal}
                        updateValue={updateUrl}
                        textIsValid={validateUrl}
                    />
                )
            }
            align={'start'}
            position={['bottom']}
            onClickOutside={closeWhenClickOutsideModal}
            isOpen={isOpen}
        >
            <LinkTag
                link={url}
                initialTitle={objectName}
                onPress={!quillTextInputIsCalendarTask[editorId] && openModal}
                shortTags={needShortTags()}
                expandInNote={true}
                setObjectType={setObjectType}
                setObjectData={setObjectData}
                setObjectProjectId={setObjectProjectId}
                setIsPrivate={setIsPrivate}
                setUserIsMember={setUserIsMember}
            />
        </AppPopover>
    )
}
